import { classifyPhysicalLayout } from './physical-layout.js?v=20260816-physical2';


export function normalizeName(value) {
  return String(value || '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function buildStructuredOverlayIndex(raw) {
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.cases) ? raw.cases : []);
  const byName = new Map();
  const byKey = new Map();
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const name = normalizeName(entry.name);
    if (name && !byName.has(name)) byName.set(name, entry);
    const key = String(entry._case_key || entry.case_key || '').trim().toLowerCase();
    if (key && !byKey.has(key)) byKey.set(key, entry);
  }
  return { byName, byKey, count: list.length };
}

export function getStructuredOverlay(caseObj, index) {
  if (!caseObj || !index) return null;
  const key = String(caseObj.caseKey || caseObj._case_key || caseObj.case_key || '').trim().toLowerCase();
  if (key && index.byKey?.has(key)) return index.byKey.get(key);
  return index.byName?.get(normalizeName(caseObj.name)) || null;
}

function modeIdOf(mode) {
  return String(mode?.mode_id ?? mode?.id ?? '').trim();
}

export function getActualModes(overlay) {
  const source=(Array.isArray(overlay?.modes) ? overlay.modes : [])
    .filter(mode => mode && typeof mode === 'object' && modeIdOf(mode));
  const out=[];
  for(const mode of source){
    const id=modeIdOf(mode);
    const name=String(mode?.mode_name||mode?.name||id);
    const lower=name.toLowerCase();
    const combinedClassicFlipped=/\b(classic|reference)\b/.test(lower)
      && /\b(flipped|inverted|inverse)\b/.test(lower)
      && /[\/|]/.test(name)
      && mode?.spatial?.flipped===undefined
      && !Array.isArray(mode?.variants);
    if(!combinedClassicFlipped){out.push(mode);continue;}

    // Legacy compilers sometimes merged Classic + Flipped because the dimensional limits
    // are identical. They are nevertheless distinct spatial states. Expand that combined
    // semantic record generically at runtime so no case-name exception is required. New
    // cases_structured output should emit these as two explicit modes instead.
    const baseSpatial={...(mode?.spatial||{}),template:mode?.spatial?.template||'reference'};
    out.push({
      ...mode,
      mode_id:`${id}__classic`,
      mode_name:'Classic Layout',
      spatial:{...baseSpatial,flipped:false},
      _sourceModeId:id,
      _expandedSpatialVariant:'classic'
    });
    out.push({
      ...mode,
      mode_id:`${id}__flipped`,
      mode_name:'Flipped Layout',
      spatial:{...baseSpatial,flipped:true},
      _sourceModeId:id,
      _expandedSpatialVariant:'flipped'
    });
  }
  return out;
}

function findPreferredInitialMode(modes) {
  if (!modes.length) return null;
  const explicitlyDefault = modes.find(mode =>
    mode?.is_default === true || mode?.default === true || mode?.isDefault === true ||
    String(mode?.default || '').toLowerCase() === 'true'
  );
  return modeIdOf(explicitlyDefault || modes[0]) || null;
}

function descriptorFromValue(value, fallbackFamily = 'reference') {
  const raw = String(value || '').trim().toLowerCase();
  const fallback = String(fallbackFamily || 'reference').toLowerCase();
  const tokenText = raw.replace(/[_-]+/g,' ');
  const family = raw.includes('sandwich') ? 'sandwich'
    : raw.includes('console') ? 'console'
      : raw.includes('reference') || raw.includes('classic') ? 'reference'
        : fallback.includes('sandwich') ? 'sandwich'
          : fallback.includes('console') ? 'console' : 'reference';
  const familyExplicit = /\b(sandwich|console|reference|classic)\b/.test(tokenText);
  return { family, familyExplicit, raw };
}

function baseSpatialDescriptor(caseObj, overlay) {
  const declared = overlay?.spatial?.template || overlay?.layoutFamily || caseObj?.style || caseObj?.layout || 'reference';
  const descriptor=descriptorFromValue(declared, 'reference');
  const physical=classifyPhysicalLayout(caseObj,{family:descriptor.family});
  return {...descriptor,physical};
}

function modeTraits(activeMode, baseDescriptor=null) {
  const modeText = `${activeMode?.mode_name || ''} ${activeMode?.name || ''} ${activeMode?.mode_id || ''}`.trim().toLowerCase();
  const baseText = String(baseDescriptor?.raw || '').trim().toLowerCase();
  const text = `${baseText} ${modeText}`.trim();
  const tokenText = text.replace(/[_-]+/g,' ');
  const slot = modeText.match(/(\d+(?:\.\d+)?)\s*[- ]?slot/);
  const vertical = /\b(?:vertical|vert)\b/.test(tokenText);
  // "Vertical" in a mode name is not enough to mean a whole-case reference transform.
  // In flexible cases (notably slot-count VGPU/spine modes) it describes a component/topology
  // relationship. Whole-reference rotation is established by physical case orientation, an
  // explicit spatial.verticalReference flag, or unambiguous "vertical reference/classic" text.
  const modeTokenText = modeText.replace(/[_-]+/g,' ');
  const explicitVerticalReferenceText = /\b(?:vertical|vert)\s+(?:reference|classic|case)\b|\b(?:reference|classic)\s+(?:vertical|vert)\b/.test(modeTokenText);
  const explicitVerticalGpuText = /\b(?:vertical|vert)\s+gpu\b|\bgpu\s+(?:vertical|vert)\b/.test(tokenText);
  // A reference-family mode described as a vertical N-slot GPU layout is a riser/GPU
  // relationship, not a sandwich conversion. Keep the motherboard on the reference tray.
  // This is generic mode semantics: an explicitly declared spatial.template still wins.
  const referenceSlotVerticalGpu = String(baseDescriptor?.family||'').toLowerCase()==='reference'
    && vertical
    && Number.isFinite(Number(slot?.[1]))
    && Number(slot?.[1])>0
    && /\bgpu\b/.test(modeText);
  const verticalGpuOnly = (explicitVerticalGpuText || referenceSlotVerticalGpu)
    && !explicitVerticalReferenceText;
  const explicitFlipped = activeMode?.spatial?.flipped ?? activeMode?.flipped ?? activeMode?.isFlipped;
  const flippedWord = /\b(flipped|inverted|inverse)\b/.test(modeText);
  const pairedClassicFlipped = flippedWord
    && /\b(classic|reference)\b/.test(modeText)
    && /[\/|]/.test(modeText);
  return {
    raw: text,
    modeRaw: modeText,
    caseRaw: baseText,
    vertical,
    verticalReference: explicitVerticalReferenceText && !verticalGpuOnly,
    verticalGpuOnly,
    // A combined capability label such as "Classic / Flipped Layout" is not itself a
    // request to invert the active build. Default to canonical classic; explicit structured
    // state can still request the flipped variant.
    flipped: typeof explicitFlipped === 'boolean' ? explicitFlipped : (flippedWord && !pairedClassicFlipped),
    supportsFlippedVariant: pairedClassicFlipped,
    // `vertical` is lexical/mode intent. `uprightSandwich` is a physical packaging frame and
    // is resolved later from the case's actual orientation; do not conflate the two.
    uprightSandwich: false,
    slotCount: slot ? Number(slot[1]) : null
  };
}

export function createCaseSpatialState(caseObj, overlay) {
  const modes = getActualModes(overlay);
  const base = baseSpatialDescriptor(caseObj, overlay);
  const initialModeId = findPreferredInitialMode(modes);
  const physical=base.physical || classifyPhysicalLayout(caseObj,{family:base.family});
  return {
    // There is deliberately no synthetic "BASE / DEFAULT" mode. Cases with real structured
    // modes begin on the first/explicit-default mode; cases without modes remain mode-less.
    modeId: initialModeId,
    fixedStep: null,
    layoutFamily: base.family,
    // Physical case orientation is identity, not a selectable mode. This mirrors /db:
    // reference + H>L is a vertical-reference case even when cases_structured has no mode.
    layoutTransform: physical.layoutId === 'vertical_reference' ? 'vertical' : 'standard',
    physicalLayoutId: physical.layoutId,
    physicalOrientation: physical.orientation,
    modeSource: initialModeId ? 'structured_initial' : 'base',
    overlayVersion: Number(overlay?._schema_version || overlay?.schema_version || 0)
  };
}

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function baseLimits(caseObj) {
  return {
    maxGPU: finite(caseObj?.maxGPU) ?? 0,
    maxGPUH: finite(caseObj?.maxGPUHeight ?? caseObj?.maxGPUH) ?? 0,
    maxGPUT: finite(caseObj?.maxThick ?? caseObj?.maxGPUT) ?? 0,
    maxCPU: finite(caseObj?.maxCPU) ?? 0,
    maxAIO: finite(caseObj?.maxAIO) ?? 0,
    maxPSU: finite(caseObj?.maxPSU) ?? 0
  };
}

function normalizeMobo(value) {
  const raw = String(value || '').toLowerCase().replace(/micro[-_ ]?atx|m[-_ ]?atx/g, 'matx').replace(/mini[-_ ]?itx/g, 'itx');
  if (raw.includes('matx')) return 'MATX';
  if (raw.includes('dtx')) return 'DTX';
  if (raw.includes('atx')) return 'ATX';
  return raw.includes('itx') ? 'ITX' : raw.toUpperCase();
}
function normalizePsu(value) {
  const raw = String(value || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  if (raw.includes('sfx_l') || raw.includes('sfxl')) return 'SFX-L';
  if (raw.includes('flex')) return 'FLEX';
  if (raw === 'atx' || (raw.includes('atx') && !raw.includes('sfx'))) return 'ATX';
  if (raw.includes('sfx')) return 'SFX';
  return raw.toUpperCase();
}

function compareThreshold(actual, op, expected) {
  const a = finite(actual), e = finite(expected);
  if (a === null || e === null) return false;
  if (op === 'gt') return a > e;
  if (op === 'gte') return a >= e;
  if (op === 'lt') return a < e;
  if (op === 'lte') return a <= e;
  return false;
}

function conditionValues(value) {
  return Array.isArray(value) ? value : [value];
}
function anyNormalizedMatch(actual, expected, normalizer) {
  const a = normalizer(actual);
  if (!a) return false;
  return conditionValues(expected).some(value => normalizer(value) === a);
}
function normalizeCooling(value) {
  const raw=String(value || '').trim().toLowerCase();
  if (raw.includes('aio') || raw.includes('liquid') || raw.includes('water')) return 'liquid';
  if (raw.includes('air')) return 'air';
  return raw;
}
function normalizeRadFan(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 17) return 'slim';
    if (value >= 20) return 'standard';
  }
  const raw=String(value || '').trim().toLowerCase();
  if (raw.includes('slim') || raw === '15' || raw === '15mm') return 'slim';
  if (raw.includes('standard') || raw.includes('normal') || raw === '25' || raw === '25mm') return 'standard';
  return raw;
}

export function ruleMatches(rule, selections = {}) {
  const c = rule?.conditions || {};
  for (const [key, value] of Object.entries(c)) {
    if (value === null || value === undefined || value === '') continue;
    if (key === 'mobo' && !anyNormalizedMatch(selections.mobo, value, normalizeMobo)) return false;
    else if (key === 'psu' && !anyNormalizedMatch(selections.psu, value, normalizePsu)) return false;
    else if (key === 'cooling' && !anyNormalizedMatch(selections.cooling, value, normalizeCooling)) return false;
    else if (key === 'rad' || key === 'radiator') {
      const actual=finite(selections.rad);
      if (!conditionValues(value).some(v => finite(v) === actual)) return false;
    } else if (key === 'radFan') {
      if (!anyNormalizedMatch(selections.radFan, value, normalizeRadFan)) return false;
    } else {
      const m = key.match(/^(gpuT|gpuL|gpuH|cpuH)_(gt|gte|lt|lte)$/);
      if (m && !compareThreshold(selections[m[1]], m[2], value)) return false;
    }
  }
  return true;
}

function applyOverrides(target, overrides, restrictive = false) {
  if (!overrides || typeof overrides !== 'object') return;
  const aliases = { maxThick: 'maxGPUT' };
  for (const [rawKey, rawValue] of Object.entries(overrides)) {
    const key = aliases[rawKey] || rawKey;
    if (!(key in target)) continue;
    const value = finite(rawValue);
    if (value === null || value < 0) continue;
    if (restrictive && target[key] > 0 && value > 0) target[key] = Math.min(target[key], value);
    else target[key] = value;
  }
}

function inferModeSpatialDescriptor(activeMode, baseDescriptor, overlay = null) {
  if (!activeMode) return { family:baseDescriptor.family, familyExplicit:false, raw:baseDescriptor.raw, traits:modeTraits(null,baseDescriptor) };
  const explicit = activeMode?.spatial?.template || activeMode?.layoutFamily || activeMode?.layout || activeMode?.style || '';
  const text = `${activeMode?.mode_name || ''} ${activeMode?.name || ''} ${activeMode?.mode_id || ''}`.trim();
  const combined = `${explicit} ${text}`.trim();
  const parsed = descriptorFromValue(combined, baseDescriptor.family);
  const traits=modeTraits(activeMode,baseDescriptor);

  // Mode names do not change topology family by themselves. In particular a reference case
  // with `Vertical Layout (N-Slot GPU)` keeps its reference motherboard/tray and changes only
  // the GPU/riser relationship. A true sandwich conversion must be declared explicitly in
  // cases_structured (spatial.template/layoutFamily/layout/style). This keeps topology data-
  // driven and prevents a global hasSpine flag from reclassifying flexible reference cases.
  const referenceVerticalGpuMode = !parsed.familyExplicit
    && baseDescriptor.family==='reference'
    && traits.verticalGpuOnly;
  const family = parsed.familyExplicit ? parsed.family : baseDescriptor.family;
  if(family==='sandwich'){
    traits.verticalReference=false;
    traits.vertical=true;
    traits.sandwichModeVertical=true;
  }
  if(referenceVerticalGpuMode){
    traits.verticalReference=false;
    traits.verticalGpuOnly=true;
  }

  return {
    family,
    familyExplicit: parsed.familyExplicit || referenceVerticalGpuMode,
    familyInference: referenceVerticalGpuMode ? 'reference_vertical_gpu_mode' : (parsed.familyExplicit ? 'explicit' : 'base'),
    raw: parsed.raw,
    traits
  };
}

export function resolveEffectiveCaseState(caseObj, overlay, spatialState, selections = {}) {
  // Keep an immutable copy of the chassis' native reference limits. Some modes alter GPU
  // orientation/clearance without relocating the motherboard mounting holes. The solver may
  // therefore need the base GPU corridor to preserve that fixed tray while applying the
  // mode-specific GPU dimensions separately.
  const nativeLimits = baseLimits(caseObj);
  const limits = { ...nativeLimits };
  const matchedRules = [];
  const modes = getActualModes(overlay);
  let activeMode = null;
  const requestedModeId = spatialState?.modeId == null ? null : String(spatialState.modeId);
  if (requestedModeId) activeMode = modes.find(m => modeIdOf(m) === requestedModeId)
    || modes.find(m => String(m?._sourceModeId||'')===requestedModeId && m?._expandedSpatialVariant==='classic')
    || null;
  // Old persisted state may still say "default". It is not treated as a fabricated mode.
  if (!activeMode && requestedModeId === 'default') activeMode = null;
  if (!activeMode && modes.length) activeMode = modes[0];
  if (activeMode) applyOverrides(limits, activeMode.overrides || {}, false);

  // Resolve the active mode's family before spine/fixed-step authority. A mode can be a
  // sandwich topology even when the base case record is REFERENCE (e.g. a VGPU slot mode).
  const baseDescriptor = baseSpatialDescriptor(caseObj, overlay);
  const modeDescriptor = inferModeSpatialDescriptor(activeMode, baseDescriptor, overlay);

  const modeHasSpine = typeof activeMode?.hasSpine === 'boolean' ? activeMode.hasSpine : null;
  const modeSteps = Array.isArray(activeMode?.fixedSteps) ? activeMode.fixedSteps : [];
  const modeImpliedSpine = modeDescriptor.family === 'sandwich'
    && (modeDescriptor.familyInference === 'vertical_slot_spine_mode' || modeSteps.length > 0);
  const resolvedHasSpine = modeHasSpine !== null ? modeHasSpine : (modeImpliedSpine || !!overlay?.hasSpine);
  const globalSteps = Array.isArray(overlay?.fixedSteps) ? overlay.fixedSteps : [];
  const stepPool = modeSteps.length ? modeSteps : (resolvedHasSpine ? globalSteps : []);

  let fixedStep = null;
  let fixedStepSource = null;
  if (resolvedHasSpine) {
    if (spatialState?.fixedStep) {
      fixedStep = stepPool.find(s => String(s.slotName || s.id || '') === String(spatialState.fixedStep)) || null;
      fixedStepSource = fixedStep ? 'user' : null;
    } else if (activeMode && stepPool.length) {
      // Some records expose the physical spine state twice: once as a case mode and once in
      // fixedSteps.  If the labels match, they are one physical state.  This prevents a
      // selected "4-Slot Mode" from silently retaining the 3-slot/base CPU/GPU allocation.
      const norm = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'');
      const modeKeys = new Set([modeIdOf(activeMode), activeMode?.mode_name, activeMode?.name, activeMode?.slotName].map(norm).filter(Boolean));
      fixedStep = stepPool.find(step => [step?.slotName,step?.id,step?.mode_id,step?.name].map(norm).some(key=>key&&modeKeys.has(key))) || null;
      fixedStepSource = fixedStep ? 'active_mode_match' : null;

      // Mode labels often wrap the same physical spine state in descriptive wording
      // (e.g. "Vertical Layout (4-Slot GPU)") while fixedSteps are simply "4-Slot".
      // Match the explicit slot count before falling back to AUTO/NONE.
      if (!fixedStep && Number.isFinite(Number(modeDescriptor?.traits?.slotCount))) {
        const wanted=Number(modeDescriptor.traits.slotCount);
        const slotNumber=value=>{
          const m=String(value || '').match(/(\d+(?:\.\d+)?)\s*[- ]?slot/i);
          return m ? Number(m[1]) : null;
        };
        fixedStep = stepPool.find(step => [step?.slotName,step?.id,step?.mode_id,step?.name]
          .some(value => slotNumber(value) === wanted)) || null;
        fixedStepSource = fixedStep ? 'active_mode_slot_match' : null;
      }
    }
    if (fixedStep) {
      const cpu = finite(fixedStep.maxCPU), gpuT = finite(fixedStep.maxThick ?? fixedStep.maxGPUT);
      if (cpu !== null) limits.maxCPU = cpu;
      if (gpuT !== null) limits.maxGPUT = gpuT;
    }
  }

  const matchedRuleFailures = [];
  const matchedRuleWarnings = [];
  for (const rule of (overlay?.rules || [])) {
    if (!ruleMatches(rule, selections)) continue;
    matchedRules.push(rule);
    const overrides=rule.overrides || null;
    applyOverrides(limits, overrides || {}, true);

    const violations = overrides ? [
      overrides.maxGPUT !== undefined && finite(selections.gpuT) !== null && finite(selections.gpuT) > limits.maxGPUT,
      overrides.maxGPU !== undefined && finite(selections.gpuL) !== null && finite(selections.gpuL) > limits.maxGPU,
      overrides.maxGPUH !== undefined && finite(selections.gpuH) !== null && finite(selections.gpuH) > limits.maxGPUH,
      overrides.maxCPU !== undefined && finite(selections.cpuH) !== null && finite(selections.cpuH) > limits.maxCPU,
      overrides.maxAIO !== undefined && finite(selections.rad) !== null && finite(selections.rad) > limits.maxAIO,
      overrides.maxPSU !== undefined && finite(selections.psuL) !== null && finite(selections.psuL) > limits.maxPSU
    ].some(Boolean) : false;
    // Keep spatial-state diagnostics aligned with /db deterministic rule authority.
    if (rule?.fail_message && (!overrides || violations)) matchedRuleFailures.push(String(rule.fail_message));
    if (rule?.warn_message) matchedRuleWarnings.push(String(rule.warn_message));
  }

  // Physical orientation is normalized exactly as /db does it: family and orientation are
  // independent. A separate tall reference case therefore does not need a synthetic mode or
  // case-specific exception; reference + H>L is vertical_reference. Sandwich + H>L remains
  // vertical_sandwich in real chassis coordinates. Explicit spatial metadata can still override.
  const resolvedTraits = { ...modeDescriptor.traits };
  const physicalLayout=classifyPhysicalLayout(caseObj,{family:modeDescriptor.family});
  const explicitVerticalSandwich = activeMode?.spatial?.verticalSandwich ?? overlay?.spatial?.verticalSandwich;
  const explicitVerticalReference = activeMode?.spatial?.verticalReference ?? overlay?.spatial?.verticalReference;
  const explicitVertical = activeMode?.spatial?.vertical ?? overlay?.spatial?.vertical;

  if (physicalLayout.vertical) {
    resolvedTraits.vertical = true;
    if (modeDescriptor.family === 'reference') resolvedTraits.verticalReference = true;
    if (modeDescriptor.family === 'sandwich') resolvedTraits.uprightSandwich = true;
  }

  if (explicitVertical === true) resolvedTraits.vertical = true;
  if (modeDescriptor.family === 'reference' && explicitVerticalReference === true) {
    resolvedTraits.vertical = true;
    resolvedTraits.verticalReference = true;
  }
  if (modeDescriptor.family === 'sandwich' && explicitVerticalSandwich === true) {
    resolvedTraits.vertical = true;
    // `verticalSandwich` metadata can describe a VGPU/spine mode in a non-tall case. Only
    // elevate it to the upright packaging frame when the physical chassis is actually tall.
    if (physicalLayout.vertical) resolvedTraits.uprightSandwich = true;
  }

  if (explicitVertical === false) {
    resolvedTraits.vertical = false;
    if (modeDescriptor.family === 'reference') resolvedTraits.verticalReference = false;
  }
  if (modeDescriptor.family === 'reference' && explicitVerticalReference === false) {
    resolvedTraits.verticalReference = false;
  }
  if (modeDescriptor.family === 'sandwich' && explicitVerticalSandwich === false) {
    // `vertical` may still be a mode-level component relationship; only suppress the
    // physical upright-sandwich packaging frame.
    resolvedTraits.uprightSandwich = false;
    resolvedTraits.vertical = !!modeDescriptor.traits.vertical;
  }

  return {
    limits,
    baseLimits:nativeLimits,
    activeMode,
    fixedStep,
    fixedStepSource,
    availableSteps: stepPool,
    matchedRules,
    matchedRuleFailures,
    matchedRuleWarnings,
    hasSpine: resolvedHasSpine,
    spatialHints: {
      ...(overlay?.spatial && typeof overlay.spatial === 'object' ? overlay.spatial : {}),
      ...(activeMode?.spatial && typeof activeMode.spatial === 'object' ? activeMode.spatial : {})
    },
    layoutFamily: modeDescriptor.family,
    layoutFamilyInference: modeDescriptor.familyInference || null,
    layoutFamilySource: modeDescriptor.familyInference === 'reference_vertical_gpu_mode'
      ? 'active_mode_inferred'
      : (modeDescriptor.familyExplicit ? 'active_mode_explicit' : (baseDescriptor.familyExplicit ? 'case_or_structured_declared' : 'fallback')),
    physicalLayoutId: physicalLayout.layoutId,
    physicalOrientation: physicalLayout.orientation,
    // Vertical sandwich remains a relationship trait in real chassis coordinates. Explicit
    // vertical-reference layouts are different: they are the normal reference architecture
    // rotated upright, so the solver uses a canonical reference frame and projects the whole
    // arrangement back into the case's real world axes.
    layoutTransform: modeDescriptor.family === 'reference' && resolvedTraits.verticalReference ? 'vertical' : 'standard',
    modeTraits: resolvedTraits
  };
}
